import { mapValues } from 'es-toolkit';
import { createContext, type ReactNode, useContext, useRef } from 'react';
import type { IStores, ReactStores, Stores } from 'src/common/stores';

export const StoresContext = createContext<ReactStores | null>(null);
const BoundStoresContext = createContext<IStores | null>(null);

interface StoresProviderProps {
  children: ReactNode;
  stores: Stores;
}

export function StoresProvider({ children, stores }: StoresProviderProps) {
  const storesRef = useRef<ReactStores | null>(null);

  if (storesRef.current === null) {
    storesRef.current = mapValues(stores.instance, (store) => store?.use) as ReactStores;
  }

  return (
    <BoundStoresContext.Provider value={stores.instance}>
      <StoresContext.Provider value={storesRef.current}>{children}</StoresContext.Provider>
    </BoundStoresContext.Provider>
  );
}

export function useStores() {
  const stores = useContext(StoresContext);

  if (!stores) {
    throw new Error('useStores must be used within a StoresProvider');
  }

  return stores;
}

type StoreState<Name extends keyof IStores> = ReturnType<IStores[Name]['getState']>;

/**
 * Subscribes to a value derived from a store's state, and re-renders only when that value changes.
 * Prefer this over `useStores()` when many instances of a component each care about a small,
 * instance-specific slice of shared state (e.g. whether this tile is the one playing).
 */
export function useStoreSelector<Name extends keyof IStores, Selected>(
  name: Name,
  selector: (state: StoreState<Name>) => Selected,
): Selected {
  const stores = useContext(BoundStoresContext);

  if (!stores) {
    throw new Error('useStoreSelector must be used within a StoresProvider');
  }

  const useBoundStore = stores[name] as unknown as (
    selector: (state: StoreState<Name>) => Selected,
  ) => Selected;

  return useBoundStore(selector);
}
